import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import StockPageHeader from "../../../components/StockPageHeader";
import { cancelStockCount, getStockCounts } from "../api/stockCountsApi";
import CancelStockCountDialog from "../components/CancelStockCountDialog";
import StockCountCardShell from "../components/StockCountCardShell";
import StockCountRequestModal from "../components/StockCountRequestModal";
import StockCountsList from "../components/StockCountsList";
import { getStockCountErrorMessage } from "../utils/stockCountError";
import {
  normalizeStockCount,
  normalizeStockCountList,
  STOCK_COUNT_STATUS,
} from "../utils/stockCountModel";

const LIST_PATH = "/stock/operacion/conteo-de-stock";
const CANCELLABLE_STATUSES = new Set([
  STOCK_COUNT_STATUS.PENDING,
  STOCK_COUNT_STATUS.IN_PROGRESS,
  STOCK_COUNT_STATUS.REALIZED,
]);

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data) &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

export default function ConteoDeStockPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const initialPage = useRef(Number(location.state?.page) || 1).current;
  const [stockCounts, setStockCounts] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [operationError, setOperationError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [stockCountToCancel, setStockCountToCancel] = useState(null);
  const [cancellingStockCountId, setCancellingStockCountId] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || ""
  );

  const fetchStockCounts = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      setLoading(true);
      try {
        const response = await getStockCounts({
          page,
          signal,
        });
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const result = normalizeStockCountList(unwrapHttp(response));
        if (result.meta.totalPages > 0 && page > result.meta.totalPages) {
          setPage(result.meta.totalPages);
          return;
        }
        setStockCounts(result.data);
        setTotalItems(result.meta.totalItems);
        setTotalPages(result.meta.totalPages);
        setError("");
      } catch (fetchError) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setStockCounts([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(
          getStockCountErrorMessage(
            fetchError,
            "No se pudieron cargar los conteos de stock."
          )
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [page]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchStockCounts(controller.signal);
    return () => controller.abort();
  }, [fetchStockCounts, reloadVersion]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin conteos para mostrar";
    return `Mostrando ${stockCounts.length} de ${totalItems} conteos`;
  }, [stockCounts.length, totalItems]);

  function handleView(stockCount) {
    if (cancellingStockCountId) return;
    navigate(`${LIST_PATH}/${stockCount.id}`, {
      state: { page },
    });
  }

  function handleOpenCancel(stockCount) {
    if (
      cancellingStockCountId ||
      !stockCount?.id ||
      !CANCELLABLE_STATUSES.has(stockCount.status)
    ) {
      return;
    }
    setCancelError("");
    setStockCountToCancel(stockCount);
  }

  function handleCloseCancel() {
    if (cancellingStockCountId) return;
    setCancelError("");
    setStockCountToCancel(null);
  }

  async function handleConfirmCancel() {
    const stockCountId = stockCountToCancel?.id;
    if (!stockCountId || cancellingStockCountId) return;

    setCancellingStockCountId(stockCountId);
    setCancelError("");
    setError("");
    setOperationError("");
    setSuccessMessage("");

    try {
      const response = await cancelStockCount(stockCountId);
      const cancelled = unwrapHttp(response);
      const normalized = normalizeStockCount({
        ...(stockCountToCancel || {}),
        ...(cancelled && typeof cancelled === "object" ? cancelled : {}),
        status: cancelled?.status || STOCK_COUNT_STATUS.CANCELLED,
      });

      setStockCounts((current) =>
        current.map((stockCount) =>
          stockCount.id === stockCountId ? normalized : stockCount
        )
      );
      setStockCountToCancel(null);
      setSuccessMessage("Conteo cancelado correctamente.");
      setReloadVersion((current) => current + 1);
    } catch (cancelErrorResponse) {
      const status =
        cancelErrorResponse?.response?.status || cancelErrorResponse?.status;
      if (status === 409) {
        setStockCountToCancel(null);
        setOperationError(
          "El conteo cambio de estado recientemente y no se pudo cancelar. Actualizamos la informacion para que veas el estado vigente."
        );
        setReloadVersion((current) => current + 1);
      } else {
        setCancelError(
          getStockCountErrorMessage(
            cancelErrorResponse,
            "No se pudo cancelar el conteo."
          )
        );
      }
    } finally {
      setCancellingStockCountId(null);
    }
  }

  function handleRequestCreated({ successMessage: message } = {}) {
    setRequestDialogOpen(false);
    setSuccessMessage(
      message || "La solicitud de conteo se creó correctamente."
    );
    setPage(1);
    setReloadVersion((current) => current + 1);
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Conteo de Stock"
        onBack={() => navigate("/stock/operacion")}
      />

      <StockCountCardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "primary.900",
                fontSize: 17,
                fontWeight: 800,
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            >
              Listas de Conteo
            </Typography>

            <Button
              type="button"
              variant="contained"
              onClick={() => setRequestDialogOpen(true)}
              startIcon={<AddIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-end" },
                minWidth: 190,
                height: 38,
                px: 3,
                borderRadius: "33px",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 700,
                whiteSpace: "nowrap",
                
                backgroundColor: "#D1F4E0", 
                color: "#133B24",           
                boxShadow: "none",          
                "&:hover": {
                  backgroundColor: "#B8EED0", 
                  boxShadow: "none",
                },
              }}
            >
              Solicitar conteo
            </Button>
          </Stack>

          {successMessage ? (
            <Alert
              severity="success"
              onClose={() => setSuccessMessage("")}
              sx={{ borderRadius: "16px", fontWeight: 650 }}
            >
              {successMessage}
            </Alert>
          ) : null}

          {operationError ? (
            <Alert
              severity="error"
              onClose={() => setOperationError("")}
              sx={{ borderRadius: "16px", fontWeight: 650 }}
            >
              {operationError}
            </Alert>
          ) : null}

          {error ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setReloadVersion((current) => current + 1)}
                >
                  Reintentar
                </Button>
              }
              sx={{ borderRadius: "16px", fontWeight: 650 }}
            >
              {error}
            </Alert>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "auto",
              pr: 0.25,
            }}
          >
            <StockCountsList
              rows={stockCounts}
              loading={loading || Boolean(cancellingStockCountId)}
              cancellingStockCountId={cancellingStockCountId}
              onView={handleView}
              onCancel={handleOpenCancel}
            />
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </StockCountCardShell>

      <StockCountRequestModal
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        onCreated={handleRequestCreated}
      />
      <CancelStockCountDialog
        open={Boolean(stockCountToCancel)}
        loading={cancellingStockCountId === stockCountToCancel?.id}
        error={cancelError}
        onClose={handleCloseCancel}
        onConfirm={handleConfirmCancel}
      />
    </Stack>
  );
}
